import { useCallback, useMemo } from 'react';

import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';

import cx from 'classnames';
import debounce from 'lodash/debounce';
import _find from 'lodash/find';

import {
  GREATER_THAN_OR_EQUAL_TO_ZERO,
  GREATER_THAN_ZERO_OR_BLANK,
  maxIntegerValue,
} from '~/components/common/form/commonValidations';
import { MutatingForm } from '~/components/common/form/formWithMutators';
import { NumberInputWithCommas } from '~/components/common/zolaUI/NumberInputWithCommas/NumberInputWithCommas';
import { useSearchResultsPageContext } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import { useVendorSearchNavigation } from '~/pages/couples/explore/components/SearchResults/contexts/VendorSearchNavigationContext';
import { extractMinMaxFromSpecialFacets } from '~/pages/couples/explore/util/createVendorSearchFromFilters';
import { extractMinMaxFromSlug } from '~/pages/couples/explore/util/helpers';
import { formatStringToNumber } from '~/util/formatters';

import { getFacetFromPriceRange } from './getFacetFromPriceRange';

import styles from './PriceFacetForm.module.less';

type PriceFacetFormValues = {
  priceMinimum?: string;
  priceMinimumAsCurrency?: string;
  priceMaximum?: string;
  priceMaximumAsCurrency?: string;
};

const maxGreaterThanMin = (value: string | undefined, values: PriceFacetFormValues) => {
  if (values.priceMaximum && value) {
    const minValue = formatStringToNumber(value);
    const maxValue = formatStringToNumber(values.priceMaximum);
    if (maxValue && minValue && minValue >= maxValue) {
      return `Minimum can't be higher than maximum`;
    }
  }
  return undefined;
};

const lessThanOneMillion = (value: string | undefined) => {
  return maxIntegerValue(1_000_000)(formatStringToNumber(value));
};

type PriceFacetFormProps = {
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  showSubmit: boolean;
  verticalStack: boolean;
  className?: string;
};

export const PriceFacetForm = (props: PriceFacetFormProps) => {
  const { setIsOpen, showSubmit, className, verticalStack } = props;
  const { selectedFacetOptions } = useSearchResultsPageContext();
  const { navigateToSrp } = useVendorSearchNavigation();

  const submitPrice = useCallback(
    (vals: PriceFacetFormValues) => {
      const { priceMinimum, priceMaximum } = vals;
      const priceMinimumValue = priceMinimum ? formatStringToNumber(priceMinimum) : undefined;
      const priceMaximumValue = priceMaximum ? formatStringToNumber(priceMaximum) : undefined;
      const selectedFacet = getFacetFromPriceRange(priceMinimumValue, priceMaximumValue);
      setIsOpen?.(false);
      navigateToSrp({
        price: selectedFacet ? extractMinMaxFromSpecialFacets([selectedFacet]) : null,
      });
    },
    [navigateToSrp, setIsOpen]
  );

  const previouslySelectedPriceFacet = useMemo(
    () => _find(selectedFacetOptions, (facet) => facet.slug.includes('price')),
    [selectedFacetOptions]
  );

  const { min, max } = useMemo(() => {
    const { slug } = previouslySelectedPriceFacet || {};
    const minAndMax = slug ? extractMinMaxFromSlug(slug) : null;
    return minAndMax || { min: undefined, max: undefined };
  }, [previouslySelectedPriceFacet]);

  const initialValues = useMemo(
    () => ({
      priceMinimum: min?.toString(),
      priceMaximum: max?.toString(),
    }),
    [min, max]
  );

  // Use a debounced fn to wait until the user is finished typing before submitting
  const debouncedSubmitPrice = debounce(submitPrice, 200, {
    leading: false,
  });

  return (
    <MutatingForm
      onSubmit={submitPrice}
      initialValues={initialValues}
      render={({ handleSubmit, form, values }) => {
        const disableSubmit =
          values.priceMaximum === initialValues.priceMaximum &&
          values.priceMinimum === initialValues.priceMinimum;

        const disableClear = !(values.priceMaximum || values.priceMinimum);

        return (
          <form
            key="price-facet-form"
            onSubmit={handleSubmit}
            className={cx(styles.formContainer, className)}
          >
            <div className={cx(styles.inputsWrapper, { [styles.verticalStack]: verticalStack })}>
              <NumberInputWithCommas
                name="priceMinimum"
                data-testid="priceMinimum"
                validate={[GREATER_THAN_OR_EQUAL_TO_ZERO, lessThanOneMillion, maxGreaterThanMin]}
                displayedField="priceMinimumAsCurrency"
                type="currency"
                placeholder="Enter a number"
                label="Minimum"
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (!showSubmit) {
                    debouncedSubmitPrice({
                      priceMinimum: e.target.value,
                      priceMaximum: values.priceMaximum,
                    });
                  }
                }}
              />

              <NumberInputWithCommas
                name="priceMaximum"
                data-testid="priceMaximum"
                validate={[GREATER_THAN_ZERO_OR_BLANK, lessThanOneMillion]}
                displayedField="priceMaximumAsCurrency"
                type="currency"
                placeholder="Enter a number"
                label="Maximum"
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (!showSubmit) {
                    debouncedSubmitPrice({
                      priceMinimum: values.priceMinimum,
                      priceMaximum: e.target.value,
                    });
                  }
                }}
              />
            </div>
            {showSubmit && (
              <div className={styles.ctaWrapper}>
                <LinkV2
                  role="button"
                  type="button"
                  onClick={() => {
                    form.batch(() => {
                      form.change('priceMinimum', undefined);
                      form.change('priceMaximum', undefined);
                      form.change('priceMinimumAsCurrency', undefined);
                      form.change('priceMaximumAsCurrency', undefined);
                    });
                  }}
                  disabled={disableClear}
                >
                  Clear
                </LinkV2>
                <ButtonV3 disabled={disableSubmit} type="submit">
                  Apply
                </ButtonV3>
              </div>
            )}
          </form>
        );
      }}
    />
  );
};

export default PriceFacetForm;
